// Asks `url` for options as `q` is typed, and answers Tom Select's loader callback
// with what arrives — or with nothing, when the request fails.
function loadFrom(url) {
  return function(query, callback) {
    const separator = url.includes('?') ? '&' : '?'
    fetch(url + separator + 'q=' + encodeURIComponent(query), {headers: {Accept: 'application/json'}})
      .then(response => response.json())
      .then(items => callback(items))
      .catch(() => callback())
  }
}

up.compiler('select.selectpicker, [data-tom-select]', (element) => {
  const mode = element.dataset.tomSelectMode || 'select'

  const options = {
    plugins: [],
  }

  // Multi-select: add remove button
  if (element.multiple) {
    options.plugins.push('remove_button')
  }

  // A multi-select searched by typing keeps the search after an option is picked,
  // so the next search is typed onto the end of the last one — "srv-dc01DC02" —
  // and finds nothing. Each domain controller, and each DNS server a network's tag
  // field suggests, is found by a search of its own.
  if (element.multiple) {
    options.clearAfterSelect = true
  }

  // Search: selectpicker always had search via data-live-search, Tom Select has it by default.
  // To disable search, set data-tom-select-no-search
  if (element.dataset.tomSelectNoSearch !== undefined) {
    options.controlInput = null
  }

  // Placeholder from title attribute (bootstrap-select pattern)
  if (element.title && !element.dataset.placeholder) {
    options.placeholder = element.title
  }

  // No `maxOptions` from the markup. `data-size` was bootstrap-select's number of rows
  // shown before its menu scrolled; passed on as `maxOptions` it became a hard cap on
  // what is listed at all, and every select hid its options past the fifth until
  // something was typed.

  // Remote multi-select: picks existing records through a server-side search, so the
  // markup carries only the options already chosen. Each result is
  // `{value, text, search}`. Tom Select filters what it has loaded against its own
  // fields, so `search` — the text the server matched on — keeps a record found by
  // something its label does not show. Opening the field loads the first results
  // before anything is typed.
  if (mode === 'remote') {
    options.searchField = ['text', 'search']
    options.preload = 'focus'
    options.load = loadFrom(element.dataset.url)
  }

  // Tags mode
  if (mode === 'tags') {
    options.create = element.dataset.create !== 'false'

    // The dropdown refreshes 300ms after the last keystroke by default, and Enter
    // before then takes whichever option was highlighted a moment ago. In a field that
    // creates what is typed that is the wrong entry: "9.9.9.9" and Enter in one go
    // re-added the address just removed, which comes back as that option. Filtering
    // the options already loaded is cheap; the server is still asked on its own
    // throttle.
    options.refreshThrottle = 0
    options.plugins.push('remove_button')

    if (element.dataset.createFilter) {
      options.createFilter = new RegExp(element.dataset.createFilter)
    }

    if (element.dataset.clearButton !== undefined) {
      options.plugins.push('clear_button')
    }

    if (element.dataset.openOnFocus !== undefined) {
      options.openOnFocus = true
    }

    // Suggestions from the server beside what is typed — a network's DNS field offers
    // the addresses of the client's devices. Filtered locally on `search` as well, for
    // the reason the remote mode gives.
    if (element.dataset.url) {
      options.searchField = ['text', 'search']
      options.load = loadFrom(element.dataset.url)
      if (options.openOnFocus) options.preload = 'focus'
    }

    // Suggestions threshold
    if (element.dataset.shouldLoadThreshold) {
      const threshold = parseInt(element.dataset.shouldLoadThreshold)
      options.shouldLoad = function(query) { return query.length >= threshold }
    }

    // Warning badge for invalid items
    if (element.dataset.warningClass) {
      const regex = element.dataset.createFilter ? new RegExp(element.dataset.createFilter) : null
      options.render = options.render || {}
      options.render.item = function(data, escape) {
        const isInvalid = regex && !regex.test(data.value)
        const badgeStyle = data.badgeStyle
        const cls = (isInvalid || badgeStyle === 'danger') ? element.dataset.warningClass : ''
        return '<div class="item ' + cls + '">' + escape(data.text) + '</div>'
      }
    }
  }

  // Autocomplete mode (remote/AJAX)
  if (mode === 'autocomplete') {
    // A single value, and one the loader need not have returned. `create` used to
    // be set in tags mode only, so no autocomplete field could commit what was
    // typed — the next manufacturer nobody has heard of could be suggested against
    // but never entered. `persist: false` keeps a value the user typed and then
    // cleared out of the option list.
    options.maxItems = 1
    options.persist = false
    if (element.dataset.create !== undefined) {
      options.create = element.dataset.create !== 'false'
    }

    const url = element.dataset.url
    if (url) {
      options.load = loadFrom(url)
      if (element.dataset.labelMethod) {
        options.labelField = element.dataset.labelMethod
      }
      if (element.dataset.valueMethod) {
        options.valueField = element.dataset.valueMethod
      }
    }
  }

  // HTML rendering: render option/item labels with icons
  if (element.dataset.renderHtml !== undefined) {
    let icons = element.dataset.icons ? JSON.parse(element.dataset.icons) : {}
    options.render = options.render || {}

    function renderWithIcon(data, escape) {
      let iconClass = icons[data.value]
      let iconHtml = iconClass ? '<i class="' + iconClass + ' me-2"></i>' : ''
      return '<div>' + iconHtml + escape(data.text) + '</div>'
    }

    options.render.option = renderWithIcon
    options.render.item = renderWithIcon
  }

  // A single `<select>` always reports its first option as selected, even when
  // the server chose nothing. bootstrap-select ignored that and showed its
  // `title` as a "nothing selected" placeholder — which is why every collection
  // select still carries one (`CollectionSelectInput#blank_label`). Tom Select
  // instead adopts the implicit selection, so such a field came up already
  // showing the first option: choosing that option was then a no-op that fired
  // no `change`, and anything hanging off it (`up-validate`) never ran.
  //
  // Only when the markup carries no explicit selection at all — no
  // `option[selected]` and no blank option to land on — is the value implicit.
  if (!element.multiple && options.placeholder &&
      !element.querySelector('option[selected]') &&
      !element.querySelector('option[value=""]')) {
    element.selectedIndex = -1
  }

  const ts = new window.TomSelect(element, options)
  element.tomSelect = ts

  return () => ts.destroy()
})
